import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, TextField, IconButton, Link } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import AutocompleteWrapper from '../common/Autocomplete/AutocompleteWrapper';
import { useFetchCliniciansOptionsForUser } from './../../actions/areas/visitActuals/fetchHooks/fetchVisitActuals';
import { updateGridFilters, clearGridFilters } from '../../actions/global/gridFilters';
import { useFetchOrgUnitOptions } from './../../actions/areas/shared/fetchOptions';
import { GridFilterPages } from '../../enums/gridFilterPages';
import globals from '../../globals/communityGlobals';
import KeyboardDateSelector from '../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps } from '../../utils/keyboardSelectorFunctions';
import { isAnyDateEmpty, isDateFromBeforeDateTo, isPeriodWithinLimit, isValidDate } from '../../utils/dates';
import { ValidationCodes } from '../../enums/ValidationCodes';
import validationRegister from '../../validation/validationRegister';

const autoCompleteStyle = {
  maxHeight: '30px',
};
const errors = globals.getArea('error');

export default function ActualsSearchFilters({ onSubmit, events }) {
  const defaultState = {
    selectedTeams: useSelector(
      store =>
        store.global.gridFilters.items.find(item => item.key === GridFilterPages.VisitActuals).filters.selectedTeams,
    ),
    selectedClinician: useSelector(
      store =>
        store.global.gridFilters.items.find(item => item.key === GridFilterPages.VisitActuals).filters
          .selectedClinician,
    ),
    dateFrom: (() => {
      const date = new Date(
        useSelector(
          store =>
            store.global.gridFilters.items.find(item => item.key === GridFilterPages.VisitActuals).filters.dateFrom,
        ),
      );
      return isValidDate(date) ? date : null;
    })(),
    dateTo: (() => {
      const date = new Date(
        useSelector(
          store =>
            store.global.gridFilters.items.find(item => item.key === GridFilterPages.VisitActuals).filters.dateTo,
        ),
      );
      return isValidDate(date) ? date : null;
    })(),
    nhsNumber: useSelector(
      store => store.global.gridFilters.items.find(item => item.key === GridFilterPages.VisitActuals).filters.nhsNumber,
    ),
    filtersRestrictions: useSelector(
      store =>
        store.global.gridFilters.items.find(item => item.key === GridFilterPages.VisitActuals).filters
          .filtersRestrictions,
    ),
    nhsNumberErrorMessage: null,
    reloadCliniciansData: false,
  };

  const maxRangeInMonths = defaultState.filtersRestrictions.maxRangeInMonths;
  const [state, setState] = useState(defaultState);
  const [validationErrors, setValidationErrors] = useState({});

  const clinicianOptionsModel = {
    teamIds: state.selectedTeams.map(x => x.id),
  };
  const { data: teamsOptions } = useFetchOrgUnitOptions(false);
  const { data: cliniciansOptions } = useFetchCliniciansOptionsForUser(
    clinicianOptionsModel,
    events.isAllocator,
    state.reloadCliniciansData,
  );
  const dispatch = useDispatch();

  const locale = events.locale;

  const handleClear = () => {
    dispatch(clearGridFilters({ key: GridFilterPages.VisitActuals }));
    setState({
      selectedTeams: [],
      selectedClinician: null,
      dateFrom: globals.now,
      dateTo: globals.now,
      filtersRestrictions: defaultState.filtersRestrictions,
      nhsNumber: null,
      nhsNumberErrorMessage: null,
      reloadCliniciansData: false,
    });
    setValidationErrors({});
    onSubmit({
      selectedTeams: [],
      selectedClinician: null,
      dateFrom: globals.now,
      dateTo: globals.now,
      nhsNumber: null,
    });
  };

  const handleNhsNumberChange = event => {
    const { nhsNumber, nhsNumberErrorMessage } = validationRegister.executeValidationModule(
      ValidationCodes.NHS,
      event.target.value,
    );
    setState({ ...state, nhsNumber, nhsNumberErrorMessage });
  };

  const noValidationErrors = () => {
    return !validationErrors.dateFrom && !validationErrors.dateTo;
  };

  const isValidNhs = () => state && state.nhsNumberErrorMessage === null;

  const fieldValidation = () => {
    return (
      noValidationErrors() &&
      isPeriodWithinLimit(state.dateFrom, state.dateTo, maxRangeInMonths * 30) &&
      isDateFromBeforeDateTo(state.dateFrom, state.dateTo, true) &&
      isValidNhs()
    );
  };

  const onSaveClickHandler = () => {
    onSubmit(state);
    dispatch(
      updateGridFilters({
        key: GridFilterPages.VisitActuals,
        filters: state,
      }),
    );
  };

  const onChangeDropDown = (key, values) => {
    if (key === 'selectedTeams') {
      setState({
        ...state,
        [key]: values,
        reloadCliniciansData: !state.reloadCliniciansData,
      });
    } else {
      setState({
        ...state,
        [key]: values,
      });
    }
  };

  const handleDateChange = (fieldName, date) => {
    setState({ ...state, [fieldName]: date });

    const dateFrom = fieldName === 'dateFrom' ? date : state.dateFrom;
    const dateTo = fieldName === 'dateTo' ? date : state.dateTo;
    const emptyFieldErrorMessage = 'Please enter date';

    if (!isPeriodWithinLimit(dateFrom, dateTo, maxRangeInMonths * 30)) {
      setValidationErrors(prevState => ({
        ...prevState,
        dateTo: errors.getMsg('filterPeriodLimit', [maxRangeInMonths.toString(), 'months']),
      }));
      return;
    }

    if (isAnyDateEmpty(dateFrom, dateTo)) {
      setValidationErrors({
        dateFrom: dateFrom ? null : emptyFieldErrorMessage,
        dateTo: dateTo ? null : emptyFieldErrorMessage,
      });
      return;
    }
    setValidationErrors({ from: null, to: null });
  };

  return (
    <section className="slick-page-header-section">
      <Box className="filter-section" display="flex" flexWrap="nowrap" p={0} justifyContent="flex-start" px={1}>
        <Box p={1}>
          <FilterListIcon />
        </Box>
        <Box p={1} width={80} textAlign="end">
          <span>{locale.getMsg('orgUnitsOptions')}</span>
        </Box>
        <Box p={1} width={200} flexGrow={1}>
          <AutocompleteWrapper
            fieldKey="selectedTeams"
            data={teamsOptions}
            isMultiple
            fullWidth
            limitTags={3}
            onChange={onChangeDropDown}
            style={autoCompleteStyle}
            defaultValue={state.selectedTeams}
            isCheckbox
            size="small"
          />
        </Box>
        <Box p={1} width={80} textAlign="end">
          <span>{locale.getMsg('clinicians')}</span>
        </Box>
        <Box p={1} width={200} flexGrow={1}>
          <AutocompleteWrapper
            fieldKey="selectedClinician"
            data={cliniciansOptions}
            fullWidth
            isMultiple={false}
            onChange={onChangeDropDown}
            isCheckbox={false}
            defaultValue={state.selectedClinician}
            style={autoCompleteStyle}
          />
        </Box>
        <Box p={1} textAlign="end" width={80}>
          <span>{locale.getMsg('nhsNumber')}</span>
        </Box>
        <Box p={1} width={150}>
          <TextField
            name="NhsNumber"
            value={state.nhsNumber || ''}
            fullWidth
            onChange={handleNhsNumberChange}
            error={state.nhsNumberErrorMessage !== null}
            helperText={state.nhsNumberErrorMessage}
          />
        </Box>
        <Box p={1} textAlign="end" width={80}>
          <span>{locale.getMsg('dateFrom')}</span>
        </Box>
        <KeyboardDateSelector
          id="date-picker-inline-from"
          className="calendar-picker-input"
          value={state.dateFrom}
          onChange={date => handleDateChange('dateFrom', date)}
          maxDate={state.dateTo}
          maxDateMessage=""
          errorProps={getErrorProps(validationErrors.dateFrom)}
          disableToolbar
        />
        <Box p={1} textAlign="end" width={80}>
          <span>{locale.getMsg('dateTo')}</span>
        </Box>
        <KeyboardDateSelector
          id="date-picker-inline-from"
          className="calendar-picker-input"
          value={state.dateTo}
          onChange={date => handleDateChange('dateTo', date)}
          errorProps={getErrorProps(validationErrors.dateTo)}
          minDate={state.dateFrom}
          minDateMessage={locale.getMsg('dateToBeforeDateFrom')}
          disableToolbar
        />
        <Box p={1} width={30} />
        <Box p={1}>
          <Link className="cancel-link" onClick={handleClear}>
            Clear
          </Link>
        </Box>
        <Box p={1}>
          <IconButton
            type="submit"
            size="small"
            onClick={onSaveClickHandler}
            className="search-icon"
            disabled={!fieldValidation()}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>
    </section>
  );
}
